import React, { useState, useEffect, useCallback } from 'react';
import {
  FileSignature,
  Plus,
  RefreshCw,
  Search,
  Filter,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  AlertCircle,
  FilePlus,
  Send,
  X,
  Mail,
  Download
} from 'lucide-react';
import { signingService } from '../services/api';
import SendDocumentModal from '../components/signing/SendDocumentModal';
import TemplateEditorModal from '../components/signing/TemplateEditorModal';
import EnvelopeStatusBadge from '../components/signing/EnvelopeStatusBadge';
import Notification from '../components/common/Notification';

const SigningEnvelopes = () => {
  // State
  const [envelopes, setEnvelopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  // Resend modal state
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendEnvelope, setResendEnvelope] = useState(null);
  const [resendSigners, setResendSigners] = useState([]);
  const [resending, setResending] = useState(false);

  // Download/View state
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });

  // Notification
  const [notification, setNotification] = useState({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    completed: 0,
    pending: 0
  });

  // Check if signing service is configured
  const checkServiceStatus = useCallback(async () => {
    try {
      const response = await signingService.getStatus();
      setServiceStatus(response.data);
    } catch (error) {
      console.error('Error checking signing service status:', error);
      setServiceStatus({ configured: false, message: 'Unable to check service status' });
    }
  }, []);

  // Fetch envelopes
  const fetchEnvelopes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;

      const response = await signingService.getEnvelopes(params);
      const data = response.data.envelopes || response.data || [];
      setEnvelopes(Array.isArray(data) ? data : []);

      // Calculate stats
      const total = data.length;
      const sent = data.filter(e => e.status === 'sent').length;
      const completed = data.filter(e => e.status === 'completed').length;
      const pending = data.filter(e => ['sent', 'viewed', 'partially_signed'].includes(e.status)).length;
      setStats({ total, sent, completed, pending });

    } catch (error) {
      console.error('Error fetching envelopes:', error);
      showNotification('Error', 'Failed to load signing envelopes', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  // Initial load
  useEffect(() => {
    checkServiceStatus();
    fetchEnvelopes();
  }, [checkServiceStatus, fetchEnvelopes]);

  // Refresh a specific envelope's status
  const handleRefreshStatus = async (envelopeId) => {
    setRefreshingId(envelopeId);
    try {
      await signingService.refreshEnvelopeStatus(envelopeId);
      await fetchEnvelopes();
      showNotification('Success', 'Envelope status refreshed', 'success');
    } catch (error) {
      console.error('Error refreshing envelope status:', error);
      showNotification('Error', 'Failed to refresh status', 'error');
    } finally {
      setRefreshingId(null);
    }
  };

  // Handle successful send
  const handleSendSuccess = (envelope) => {
    showNotification('Success', 'Document sent for signature', 'success');
    fetchEnvelopes();
    setShowSendModal(false);
  };

  // Handle successful template creation
  const handleTemplateSuccess = (template) => {
    showNotification('Success', `Template "${template.name}" created successfully`, 'success');
    setShowTemplateEditor(false);
  };

  // Open resend modal
  const handleOpenResend = (envelope) => {
    setResendEnvelope(envelope);
    // Initialize signers with current email addresses (only pending ones)
    const pendingSigners = (envelope.signers || [])
      .filter(s => s.status !== 'signed' && s.status !== 'declined')
      .map(s => ({
        submitter_id: s.submitter_id,
        name: s.name,
        email: s.email,
        status: s.status
      }));
    setResendSigners(pendingSigners);
    setShowResendModal(true);
  };

  // Handle resend
  const handleResend = async () => {
    if (!resendEnvelope) return;

    setResending(true);
    try {
      // Build signer updates if emails were changed
      const signerUpdates = resendSigners.map(s => ({
        submitter_id: s.submitter_id,
        email: s.email
      }));

      await signingService.resendEnvelope(resendEnvelope.id, signerUpdates);
      showNotification('Success', 'Signing invitation resent successfully', 'success');
      setShowResendModal(false);
      setResendEnvelope(null);
      fetchEnvelopes();
    } catch (error) {
      console.error('Error resending envelope:', error);
      const message = error.response?.data?.detail || 'Failed to resend invitation';
      showNotification('Error', message, 'error');
    } finally {
      setResending(false);
    }
  };

  // Update signer email in resend modal
  const handleResendEmailChange = (submitterId, newEmail) => {
    setResendSigners(prev =>
      prev.map(s =>
        s.submitter_id === submitterId ? { ...s, email: newEmail } : s
      )
    );
  };

  // Download signed document
  const handleDownload = async (envelopeId) => {
    setDownloadingId(envelopeId);
    try {
      const response = await signingService.downloadSignedDocument(envelopeId);
      const { download_url, filename } = response.data;

      // Open download URL in new tab or trigger download
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename || 'signed_document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showNotification('Success', 'Download started', 'success');
    } catch (error) {
      console.error('Error downloading signed document:', error);
      const message = error.response?.data?.detail || 'Failed to download signed document';
      showNotification('Error', message, 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  // View signed document (opens in new tab)
  const handleView = async (envelopeId) => {
    setViewingId(envelopeId);

    // Open the window synchronously (before await) to avoid popup blockers.
    // Browsers block window.open calls that happen after an async gap.
    const newTab = window.open('about:blank', '_blank');

    try {
      const response = await signingService.viewSignedDocument(envelopeId);
      const { download_url } = response.data;

      if (newTab) {
        newTab.location.href = download_url;
      } else {
        // Fallback: if the popup was still blocked, use an anchor element
        const link = document.createElement('a');
        link.href = download_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // Close the blank tab on error so the user doesn't see an empty page
      if (newTab) {
        newTab.close();
      }
      console.error('Error viewing signed document:', error);
      const message = error.response?.data?.detail || 'Failed to view signed document';
      showNotification('Error', message, 'error');
    } finally {
      setViewingId(null);
    }
  };

  // Show notification helper
  const showNotification = (title, message, variant = 'info') => {
    setNotification({ isOpen: true, title, message, variant });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter envelopes by search
  const filteredEnvelopes = envelopes.filter(envelope => {
    if (!filters.search) return true;
    const search = filters.search.toLowerCase();
    return (
      envelope.name?.toLowerCase().includes(search) ||
      envelope.signers?.some(s =>
        s.email?.toLowerCase().includes(search) ||
        s.name?.toLowerCase().includes(search)
      )
    );
  });

  // Render service not configured message
  if (serviceStatus && !serviceStatus.configured) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileSignature className="w-8 h-8 text-gray-400" />
            <h1 className="text-2xl font-bold text-gray-900">Document Signing</h1>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">Service Not Configured</h3>
                <p className="text-yellow-700 text-sm mt-1">
                  {serviceStatus.message || 'Document signing service is not configured. Please contact your administrator.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <FileSignature className="w-8 h-8 text-yellow-500" />
            <h1 className="text-2xl font-bold text-gray-900">Document Signing</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTemplateEditor(true)}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FilePlus className="w-4 h-4" />
              Create Template
            </button>
            <button
              onClick={() => setShowSendModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Send for Signature
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Envelopes</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.pending}</div>
            <div className="text-sm text-gray-500">Awaiting Signature</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-500">Completed</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.sent}</div>
            <div className="text-sm text-gray-500">Sent Today</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or recipient..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="">All Statuses</option>
              <option value="sent">Sent</option>
              <option value="viewed">Viewed</option>
              <option value="partially_signed">Partially Signed</option>
              <option value="completed">Completed</option>
              <option value="declined">Declined</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={fetchEnvelopes}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Envelopes List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-yellow-500" />
            <span className="ml-2 text-gray-500">Loading envelopes...</span>
          </div>
        ) : filteredEnvelopes.length === 0 ? (
          <div className="text-center py-12">
            <FileSignature className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No signing envelopes</h3>
            <p className="text-gray-500 mb-4">Get started by sending a document for signature.</p>
            <button
              onClick={() => setShowSendModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
            >
              <Plus className="w-4 h-4" />
              Send for Signature
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Document
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEnvelopes.map((envelope) => (
                <tr key={envelope.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileSignature className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {envelope.name || 'Untitled'}
                        </div>
                        {envelope.case_id && (
                          <div className="text-xs text-gray-500">
                            Case linked
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      {envelope.signers?.slice(0, 2).map((signer, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          {signer.status === 'signed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : signer.status === 'declined' ? (
                            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          ) : signer.status === 'viewed' ? (
                            <Eye className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-gray-700 truncate">{signer.name || 'Unnamed'}</div>
                            <div className="text-xs text-gray-500 truncate">{signer.email}</div>
                          </div>
                        </div>
                      ))}
                      {envelope.signers?.length > 2 && (
                        <div className="relative group">
                          <button className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                            +{envelope.signers.length - 2} more
                          </button>
                          {/* Tooltip showing all remaining signers */}
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-[200px] max-w-[280px]">
                              <div className="font-medium mb-2 text-gray-300">All Recipients:</div>
                              <div className="space-y-1.5">
                                {envelope.signers.map((signer, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    {signer.status === 'signed' ? (
                                      <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                                    ) : signer.status === 'declined' ? (
                                      <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                                    ) : signer.status === 'viewed' ? (
                                      <Eye className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                                    ) : (
                                      <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="truncate">{signer.name || 'Unnamed'}</div>
                                      <div className="text-gray-400 truncate">{signer.email}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {/* Arrow */}
                            <div className="absolute left-4 bottom-0 transform translate-y-full">
                              <div className="border-8 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <EnvelopeStatusBadge status={envelope.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(envelope.sent_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {/* View button - only show for completed envelopes */}
                      {envelope.status === 'completed' && (
                        <button
                          onClick={() => handleView(envelope.id)}
                          disabled={viewingId === envelope.id}
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                          title="View signed document"
                        >
                          <Eye className={`w-4 h-4 ${viewingId === envelope.id ? 'animate-pulse' : ''}`} />
                        </button>
                      )}
                      {/* Download button - only show for completed envelopes */}
                      {envelope.status === 'completed' && (
                        <button
                          onClick={() => handleDownload(envelope.id)}
                          disabled={downloadingId === envelope.id}
                          className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100"
                          title="Download signed document"
                        >
                          <Download className={`w-4 h-4 ${downloadingId === envelope.id ? 'animate-pulse' : ''}`} />
                        </button>
                      )}
                      {/* Resend button - only show for envelopes that aren't completed/voided/expired */}
                      {!['completed', 'voided', 'expired'].includes(envelope.status) && (
                        <button
                          onClick={() => handleOpenResend(envelope)}
                          className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100"
                          title="Resend invitation"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRefreshStatus(envelope.id)}
                        disabled={refreshingId === envelope.id}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Refresh status"
                      >
                        <RefreshCw className={`w-4 h-4 ${refreshingId === envelope.id ? 'animate-spin' : ''}`} />
                      </button>
                      {envelope.signers?.[0]?.signing_url && (
                        <a
                          href={envelope.signers[0].signing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                          title="Open signing page"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Send Document Modal */}
      <SendDocumentModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSuccess={handleSendSuccess}
      />

      {/* Template Editor Modal */}
      <TemplateEditorModal
        isOpen={showTemplateEditor}
        onClose={() => setShowTemplateEditor(false)}
        onSuccess={handleTemplateSuccess}
      />

      {/* Resend Modal */}
      {showResendModal && resendEnvelope && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Resend Invitation</h2>
              </div>
              <button
                onClick={() => {
                  setShowResendModal(false);
                  setResendEnvelope(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                Resend signing invitation for <span className="font-medium">{resendEnvelope.name || 'Untitled Document'}</span>
              </p>

              {resendSigners.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Recipients (pending signatures only):</p>
                  {resendSigners.map((signer) => (
                    <div key={signer.submitter_id} className="space-y-1">
                      <label className="block text-sm text-gray-600">{signer.name || 'Unnamed'}</label>
                      <input
                        type="email"
                        value={signer.email}
                        onChange={(e) => handleResendEmailChange(signer.submitter_id, e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        placeholder="Email address"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  All signers have already signed or declined.
                </p>
              )}

              {resendEnvelope.sent_at && (
                <p className="text-xs text-gray-500 mt-4">
                  Originally sent: {formatDate(resendEnvelope.sent_at)}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowResendModal(false);
                  setResendEnvelope(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleResend}
                disabled={resending || resendSigners.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {resending ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Resend Invitation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
      />
    </div>
  );
};

export default SigningEnvelopes;
